// ChatInputArea — Presentational input bar for the AI chat modal: the
// controlled message textarea, the optional voice (mic) button, and the send
// button.
//
// Extracted verbatim from `AIChatBot.js` (the in-file `ChatInputArea`
// sub-component) as part of the behavior-preserving refactor (issue #240).
// Render-only, wrapped in React.memo. Markup, styles, the
// `window.SpeechRecognition || window.webkitSpeechRecognition` mic-button
// render guard, the listening/loading style toggles, and the disabled guards
// are identical to the source.
//
// The textarea is fully controlled by the container / hook: its `value`
// (`inputMessage`) and `onChange` (`setInputMessage`) are passed in as props —
// this component holds no input state of its own, matching the source.
//
// Props:
//   inputRef          - ref attached to the textarea (owned by the container)
//   inputMessage      - controlled textarea value
//   setInputMessage   - onChange handler receiving the next value
//   handleKeyPress    - keydown/keypress handler for the textarea
//   inputPlaceholder  - textarea placeholder text
//   isLoading         - boolean, disables inputs while a request is in flight
//   isListening       - boolean, toggles the mic button's active styling
//   toggleListening   - callback to start/stop voice input
//   handleSendMessage - callback invoked when the send button is clicked
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { Send, Mic } from 'lucide-react';

export const ChatInputArea = React.memo(function ChatInputArea({
  inputRef,
  inputMessage,
  setInputMessage,
  handleKeyPress,
  inputPlaceholder,
  isLoading,
  isListening,
  toggleListening,
  handleSendMessage,
}) {
  return (
    <div style={{
      padding: '16px',
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      gap: '10px',
      alignItems: 'flex-end'
    }}>
      <textarea
        ref={inputRef}
        value={inputMessage}
        onChange={(e) => setInputMessage(e.target.value)}
        onKeyPress={handleKeyPress}
        placeholder={inputPlaceholder}
        style={{
          flex: 1,
          minHeight: '40px',
          maxHeight: '120px',
          padding: '10px 14px',
          border: '1px solid #d1d5db',
          borderRadius: '20px',
          fontSize: '14px',
          resize: 'none',
          outline: 'none',
          fontFamily: 'inherit'
        }}
        disabled={isLoading}
      />
      {/* Mic button — only if browser supports Speech API */}
      {(window.SpeechRecognition || window.webkitSpeechRecognition) && (
        <button
          onClick={toggleListening}
          disabled={isLoading}
          style={{
            width: '40px', height: '40px',
            background: isListening ? '#ef4444' : '#f3f4f6',
            color: isListening ? 'white' : '#6b7280',
            border: 'none',
            borderRadius: '50%',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s',
            flexShrink: 0,
            animation: isListening ? 'pulse 1.5s ease-in-out infinite' : 'none'
          }}
          title={isListening ? 'Stop listening' : 'Speak'}
        >
          <Mic size={16} />
        </button>
      )}
      <button
        onClick={() => handleSendMessage()}
        disabled={!inputMessage.trim() || isLoading}
        style={{
          width: '40px', height: '40px',
          background: inputMessage.trim() && !isLoading ? '#667eea' : '#d1d5db',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          cursor: inputMessage.trim() && !isLoading ? 'pointer' : 'not-allowed',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background-color 0.2s',
          flexShrink: 0
        }}
      >
        <Send size={16} />
      </button>
    </div>
  );
});

export default ChatInputArea;
